import { ErrorBoundary } from '@/error-boundary';
import { FallbackProps } from '@/error-boundary/contract';
import { NodeContext } from '@/node-editor/context';
import {
  getNodeContent,
  getSelectionWithChildren,
  useSelectors,
} from '@/node-editor/store/selectors';
import { migrateContent } from '@/node-editor/utilities';
import { NodeContentError } from '@/node-editor/validation';
import { nodeMap } from '@/nodes';
import { INode, TRenderContext } from '@/types/node';
import React, { useMemo, useCallback } from 'react';

interface IProps {
  node: INode;
  context: TRenderContext;
  conferenceId: string;
  pageId: string;
}

interface IRenderFallbackProps extends FallbackProps {
  conferenceId: string;
  pageId: string;
}

const RenderFallback = ({
  conferenceId,
  pageId,
  error,
}: IRenderFallbackProps): JSX.Element | null => {
  console.log('RenderFallback', error);

  if (error instanceof NodeContentError && error.hasMigration()) {
    console.log('Has migration!');

    const originalNode = error.getNode();
    const node = {
      ...originalNode,
      content: {
        ...migrateContent(
          error.getMigrationStrategy(),
          getNodeContent(originalNode)
        ),
      },
    };

    // Get the context from the error or use a default
    const context = error.getContext();
    const nodeComponent = error.data[context];

    if (typeof nodeComponent === 'function') {
      const element = nodeComponent({
        node,
        context,
        conferenceId,
        pageId,
        children: (node.children as INode[]) || [],
      });

      // Ensure we have a valid React element before cloning
      if (React.isValidElement(element)) {
        return React.cloneElement(element);
      }
    }

    // Fallback: return a simple error message
    return (
      <div
        style={{
          padding: '8px',
          border: '1px solid #ff6b6b',
          borderRadius: '4px',
          backgroundColor: '#ffe0e0',
        }}
      >
        <strong>Migration Error:</strong> Unable to render migrated node
      </div>
    );
  }

  return null;
};

const Node = React.memo(({ node, context, conferenceId, pageId }: IProps) => {
  const Component = useMemo(() => {
    try {
      return nodeMap[node.type]({ node, context });
    } catch (error) {
      console.error(
        'Error creating component for node type:',
        node.type,
        error
      );
      // Return a fallback component
      return () => (
        <div
          style={{
            padding: '8px',
            border: '1px solid #ffa500',
            borderRadius: '4px',
            backgroundColor: '#fff3cd',
          }}
        >
          <strong>Component Error:</strong> Unable to render node type "
          {node.type}"
        </div>
      );
    }
  }, [node.type, node, context]);

  return (
    <Component
      key={node.id}
      node={node}
      conferenceId={conferenceId}
      pageId={pageId}
      context={context}
    >
      {node.children as INode[]}
    </Component>
  );
});

export const RenderChildren = ({ children }: { children: INode[] }) => {
  const selectors = useSelectors(() => ({
    getSelectionWithChildren,
  }));

  // Memoize the render function to prevent unnecessary re-renders
  const renderNodes = useCallback(
    ({
      context,
      conferenceId,
      pageId,
    }: {
      context: TRenderContext;
      conferenceId: string;
      pageId: string;
    }) => {
      try {
        const nodes = selectors.getSelectionWithChildren(children);
        return nodes.map((node) => (
          <ErrorBoundary
            key={node.id}
            fallbackRender={(fallbackProps) => (
              <RenderFallback
                conferenceId={conferenceId}
                pageId={pageId}
                {...fallbackProps}
              />
            )}
          >
            <Node
              node={node}
              context={context}
              conferenceId={conferenceId}
              pageId={pageId}
            />
          </ErrorBoundary>
        ));
      } catch (error) {
        console.error('Error rendering children:', error);
        return (
          <div
            style={{
              padding: '8px',
              border: '1px solid #dc3545',
              borderRadius: '4px',
              backgroundColor: '#f8d7da',
            }}
          >
            <strong>Render Error:</strong> Unable to render children
          </div>
        );
      }
    },
    [selectors, children]
  );

  return (
    <NodeContext.Consumer>
      {({ context, conferenceId, pageId }) =>
        renderNodes({ context, conferenceId, pageId })
      }
    </NodeContext.Consumer>
  );
};
